import { Auth } from "@aws-amplify/auth";

const buildLuceneEndpoint = (
  searchValue,
  filterQuery,
  negatefield,
  start,
  size,
  sort,
  sortBy,
  isSearching,
  queryOptions,
  searchText
) => {
  const endpoint = process.env.REACT_APP_AWS_OPENSEARCH_ENDPOINT;

  const finalQuery = ["-_id:4epf_*", searchValue, filterQuery, negatefield]
    .filter(Boolean)
    .map(q => `(${q})`)
    .join(" AND ");

  const fields = isSearching
    ? queryOptions
    : undefined;

  const query = isSearching
    ? {
      bool: {
        must: [
          {
            query_string: {
              query: `*${searchText}*`,
              analyze_wildcard: true,
              default_operator: "AND",
              fields
            }
          },
          ...(finalQuery
            ? [
              {
                query_string: {
                  query: finalQuery
                }
              }
            ]
            : [])
        ]
      }
    }
    : {
      query_string: {
        query: finalQuery
      }
    };

  const queryBody = {
    from: start,
    size,
    track_total_hits: true,
    query,
    ...(sortBy && {
      sort: [{ [sortBy]: { order: sort } }]
    })
  };

  const sourceParam = encodeURIComponent(JSON.stringify(queryBody));

  return `${endpoint}/_search?source=${sourceParam}&source_content_type=application/json`;
};

const buildQueryDSLEndpoint = (
  searchValue,
  start,
  size,
  sort,
  sortBy,
  queryOptions
) => {
  const parsedQuery = typeof searchValue === 'string' ? JSON.parse(searchValue) : searchValue;

  const queryBody = {
    from: start,
    size,
    track_total_hits: true,
    query: parsedQuery
  };

  if (sortBy && !queryBody.sort) {
    queryBody.sort = [{ [sortBy]: { order: sort } }];
  }

  if (queryOptions?.fields && Array.isArray(queryOptions.fields) && !queryBody._source) {
    queryBody._source = queryOptions.fields;
  }

  const sourceParam = JSON.stringify(queryBody);
  return `${process.env.REACT_APP_AWS_OPENSEARCH_ENDPOINT}/_search?source=${sourceParam}&source_content_type=application/json`;
};

const processSearchResults = (data) => {
  const hits = data.hits?.hits || [];
  const count = data.hits?.total?.value || 0;

  const fields = hits.map((item) => ({
    ...item._source,
    id: item._id,
  }));

  return { data: fields, count };
};

const getEmptyResult = () => ({ data: [], count: 0 });

const getEndpointByParser = ({
  queryParser,
  searchValue,
  filterQuery,
  negatefield,
  start,
  size,
  sort,
  sortBy,
  isSearching,
  queryOptions,
  searchText
}) => {
  if (queryParser === "lucene") {
    return buildLuceneEndpoint(
      searchValue,
      filterQuery,
      negatefield,
      start,
      size,
      sort,
      sortBy,
      isSearching,
      queryOptions,
      searchText
    );
  }

  if (queryParser === "queryDsl") {
    return buildQueryDSLEndpoint(searchValue, start, size, sort, sortBy, queryOptions);
  }

  throw new Error(`query parser ${queryParser} is not supported`);
};

export const OpenSearchConfig = async ({
  start = 0,
  size = 10,
  searchValue,
  sort = "asc",
  sortBy,
  filterQuery,
  negatefield = "",
  queryOptions = [],
  isSearching = false,
  searchText = "",
  queryParser = "lucene"
}) => {
   try {
     const endpoint = getEndpointByParser({
      queryParser,
      searchValue,
      filterQuery,
      negatefield,
      start,
      size,
      sort,
      sortBy,
      isSearching,
      queryOptions,
      searchText
    });

    const authorizationid = (await Auth.currentSession())
      .getIdToken()
      .getJwtToken();

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${authorizationid}`,
      }
    });

    if (!response.ok) {
      return getEmptyResult();
    }

    const data = await response.json();
    return processSearchResults(data);


  } catch (error) {
    console.error("API error:", error);
    return getEmptyResult();
  }
};
